/**
 * One revision's side of a two-column inventory, grouped by what changed.
 *
 * Both inventories on the overview show the same shape: a heading with a
 * count, then groups whose headers stay put while the list scrolls. Every
 * group names the revision it belongs to, because the other revision's panel
 * is on screen beside it with the same group names.
 *
 * @module components/portfolio/comparison/InventoryPanel
 */

import React from "react";

import { Divider, Eyebrow, MutedText, Panel } from "@/components/ui";

export interface InventoryGroup<Item> {
  /** Heads the group, and with the revision label names it for a reader. */
  label: string;
  items: readonly Item[];
}

export interface InventoryPanelProps<
  Item,
  Group extends InventoryGroup<Item> = InventoryGroup<Item>,
> {
  /** The revision this side belongs to. Leads every accessible name here. */
  label: string;
  /** Names the panel, e.g. `module list`. Follows the revision label. */
  name: string;
  /**
   * Sits between the revision label and a group's own name. Two panels on one
   * screen share group names — both have "New additions" — so without this the
   * accessible names collide and neither resolves.
   */
  groupNamePrefix?: string;
  /** What the headline count counts. */
  noun: { one: string; many: string };
  groups: readonly Group[];
  itemKey: (item: Item) => string;
  renderItem: (item: Item, group: Group) => React.ReactNode;
  /** Shown instead of the groups when the revision holds nothing at all. */
  emptyMessage: string;
}

/**
 * @example
 * ```tsx
 * <InventoryPanel label="Revision 1.1" name="module list" noun={{ one: "module", many: "modules" }} … />
 * ```
 */
export function InventoryPanel<Item, Group extends InventoryGroup<Item>>({
  label,
  name,
  groupNamePrefix = "",
  noun,
  groups,
  itemKey,
  renderItem,
  emptyMessage,
}: InventoryPanelProps<Item, Group>): React.ReactElement {
  const total = groups.reduce((count, group) => count + group.items.length, 0);
  return (
    <Panel
      tone="inset"
      headingLevel={4}
      title={label}
      // Both inventories are headed by a revision label alone, so the region
      // needs the noun to tell the pair apart.
      aria-label={`${label} ${name}`}
      actions={
        <MutedText size="body" as="span">
          {total} {total === 1 ? noun.one : noun.many}
        </MutedText>
      }
    >
      {total === 0 ? (
        <MutedText className="mt-4">{emptyMessage}</MutedText>
      ) : (
        <div className="mt-3 max-h-[32rem] space-y-5 overflow-y-auto pr-1">
          {groups.map((group) => (
            <section
              key={group.label}
              aria-label={`${label} ${groupNamePrefix}${group.label.toLowerCase()}`}
            >
              <div className="sticky top-0 z-10 bg-base-100">
                <Eyebrow as="h5" className="py-2">
                  {group.label} ({group.items.length})
                </Eyebrow>
                <Divider />
              </div>
              <ul className="divide-y divide-base-300">
                {group.items.length === 0 ? (
                  <li className="py-3">
                    <MutedText as="span">None</MutedText>
                  </li>
                ) : null}
                {group.items.map((item) => (
                  <li key={itemKey(item)} className="flex items-start justify-between gap-3 py-3">
                    {renderItem(item, group)}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
}
